import React from 'react';
import { Check, X, MapPin, Edit, Plus, Trash2, RotateCcw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { QAgentSuggestion } from '../../../services/QAgent';
import { cn } from '@/lib/utils';
import { useI18n, type Dictionary } from '../../../i18n';

interface SuggestionCardProps {
  suggestion: QAgentSuggestion;
  onAccept: (suggestionId: string) => void;
  onReject: (suggestionId: string) => void;
  onUndo?: (suggestionId: string) => void;
  onViewInDocument?: (suggestion: QAgentSuggestion) => void;
  disabled?: boolean;
}

const getTypeIcon = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return <Edit className="h-3.5 w-3.5" />;
    case 'insert':
      return <Plus className="h-3.5 w-3.5" />;
    case 'delete':
      return <Trash2 className="h-3.5 w-3.5" />;
  }
};

const getTypeColor = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'insert':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'delete':
      return 'bg-red-100 text-red-700 border-red-200';
  }
};

const getStatusBadge = (t: Dictionary, status: QAgentSuggestion['status']) => {
  switch (status) {
    case 'accepted':
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t.qagentDocument.suggestions.accepted}</Badge>;
    case 'rejected':
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{t.qagentDocument.suggestions.rejected}</Badge>;
    default:
      return null;
  }
};

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onAccept,
  onReject,
  onUndo,
  onViewInDocument,
  disabled = false,
}) => {
  const { t } = useI18n();
  const st = t.qagentDocument.suggestions;
  const isPending = suggestion.status === 'pending';
  const isAccepted = suggestion.status === 'accepted';
  const canUndo = isAccepted && !suggestion.exportedAt && onUndo;

  // Accepted suggestions render as a compact single-line with undo
  if (canUndo) {
    return (
      <div className="border rounded-lg px-3 py-2 bg-muted/20 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className={cn("flex items-center gap-1 shrink-0", getTypeColor(suggestion.type))}>
            {getTypeIcon(suggestion.type)}
            <span className="capitalize">{st.types[suggestion.type] ?? suggestion.type}</span>
          </Badge>
          <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
          <span className="text-xs text-muted-foreground truncate">
            {suggestion.comment?.slice(0, 60) || suggestion.sectionTitle || st.applied}
            {(suggestion.comment?.length || 0) > 60 ? '...' : ''}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onUndo(suggestion.id)}
          disabled={disabled}
          className="h-6 gap-1 text-xs text-amber-600 hover:bg-amber-50 shrink-0"
        >
          <RotateCcw className="h-3 w-3" />
          {st.undo}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(
      "border rounded-lg p-3 space-y-2 transition-colors",
      isPending ? "bg-background" : "bg-muted/30 opacity-75"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className={cn("flex items-center gap-1", getTypeColor(suggestion.type))}>
            {getTypeIcon(suggestion.type)}
            <span className="capitalize">{st.types[suggestion.type] ?? suggestion.type}</span>
          </Badge>
          {suggestion.sectionTitle && (
            <span className="text-xs text-muted-foreground truncate">
              {suggestion.sectionTitle}
            </span>
          )}
        </div>
        {getStatusBadge(t, suggestion.status)}
      </div>

      {/* Diff visualization */}
      <div className="text-sm space-y-1">
        {suggestion.type === 'edit' && (
          <div className="space-y-1">
            {suggestion.oldText && (
              <div className="bg-red-50 border border-red-100 rounded px-2 py-1">
                <span className="text-red-600 line-through">{suggestion.oldText}</span>
              </div>
            )}
            {suggestion.newText && (
              <div className="bg-green-50 border border-green-100 rounded px-2 py-1">
                <span className="text-green-600">{suggestion.newText}</span>
              </div>
            )}
          </div>
        )}

        {suggestion.type === 'insert' && suggestion.newText && (
          <div className="bg-green-50 border border-green-100 rounded px-2 py-1">
            <span className="text-green-600">+ {suggestion.newText}</span>
          </div>
        )}

        {suggestion.type === 'delete' && suggestion.oldText && (
          <div className="bg-red-50 border border-red-100 rounded px-2 py-1">
            <span className="text-red-600 line-through">{suggestion.oldText}</span>
          </div>
        )}
      </div>

      {/* Comment */}
      {suggestion.comment && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
          <span className="text-base">💡</span>
          <span>{suggestion.comment}</span>
        </div>
      )}

      {/* Actions */}
      {isPending && (
        <div className="flex items-center gap-2 pt-1">
          <Button
            data-tour="suggestion-accept"
            variant="outline"
            size="sm"
            onClick={() => onAccept(suggestion.id)}
            disabled={disabled}
            className="h-7 gap-1.5 text-green-600 border-green-200 hover:bg-green-50"
          >
            <Check className="h-3.5 w-3.5" />
            {st.accept}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReject(suggestion.id)}
            disabled={disabled}
            className="h-7 gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
          >
            <X className="h-3.5 w-3.5" />
            {st.reject}
          </Button>
          {onViewInDocument && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewInDocument(suggestion)}
              disabled={disabled}
              className="h-7 gap-1.5 text-muted-foreground ml-auto"
            >
              <MapPin className="h-3.5 w-3.5" />
              {st.view}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestionCard;
